import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FiTrendingUp, FiTrendingDown, FiDollarSign, FiLayers, FiAlertTriangle,
  FiArrowUpRight, FiSend, FiFileText, FiPieChart, FiUsers, FiCopy, FiCheck,
} from 'react-icons/fi';
import { Header, StatementModal } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency, customFormatter } from '../../utils/currencyUtils';
import { displaySchoolTerm } from '../../utils/schoolYearUtils';
import StatCard from '../../components/common/StatCard';
import EnableMultiFundBanner from '../../components/Finance/EnableMultiFundBanner';
import FundSelector from '../../components/Finance/FundSelector';
import FinanceTrendChart from '../../components/Finance/FinanceTrendChart';
import * as Icons from '../../data/home';

const severityStyles = {
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
  error: 'bg-red-50 border-red-200 text-red-700',
  info: 'bg-blue-50 border-blue-200 text-blue-700',
};

// How much trend history to show when no date range is selected.
const DEFAULT_TREND_MONTHS = 6;
// The API accepts 1–24 months of trend data.
const MAX_TREND_MONTHS = 24;

// Trends are grouped by calendar month, so count the months the range touches
// rather than its exact length: 5 Jan → 20 Mar spans Jan, Feb and Mar = 3.
// Both values are YYYY-MM-DD, read directly off the string — passing them
// through Date would parse as UTC and shift the month in western timezones.
const monthsBetween = (from, to) => {
  const parse = (v) => {
    const [y, m] = String(v).slice(0, 10).split('-').map(Number);
    return y && m ? { y, m } : null;
  };
  const a = parse(from);
  const b = parse(to);
  if (!a || !b) return null;
  return (b.y - a.y) * 12 + (b.m - a.m) + 1;
};

const formatMonth = (d) =>
  new Date(d).toLocaleDateString('en-NG', { month: 'short', year: 'numeric' });

const RANGE_PRESETS = { TERM: 'term', SIX_MONTHS: '6m', ONE_YEAR: '1y', CUSTOM: 'custom' };

// Format a locally-built Date as YYYY-MM-DD. toISOString would convert to UTC
// and land on the previous day for any timezone ahead of it.
const toInputDate = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// Term dates arrive from the API as ISO strings; take the date part verbatim so
// the stored day isn't shifted by the viewer's timezone.
const isoDatePart = (s) => (typeof s === 'string' ? s.slice(0, 10) : toInputDate(s));

// First day of the month `n` months back. The day is pinned to 1 before the
// month is changed, otherwise JS rolls dates like 31 Aug minus 6 months into
// early March instead of the start of February.
const startOfMonthsAgo = (n) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Tailwind can't resolve interpolated class names, so map colours explicitly.
const ACTION_COLORS = {
  indigo: 'bg-indigo-50 text-indigo-600',
  blue: 'bg-blue-50 text-blue-600',
  slate: 'bg-slate-100 text-slate-600',
  red: 'bg-red-50 text-red-600',
  green: 'bg-green-50 text-green-600',
  amber: 'bg-amber-50 text-amber-600',
};

const FinanceDashboard = () => {
  useKYCStatusRedirect('/finance/dashboard');
  const { school, refreshSchool } = useStateContext();
  const schoolId = school?._id;
  const schoolAccount = school?.account_number;
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;

  const [dashboard, setDashboard] = useState(null);
  const [balance, setBalance] = useState(null);
  const [payout, setPayout] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [fundId, setFundId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [dismissedWarnings, setDismissedWarnings] = useState([]);
  const [isStatementOpen, setIsStatementOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preset, setPreset] = useState(RANGE_PRESETS.TERM);

  // The dashboard response reports this too, and is fresher than the cached
  // school object — which can lag if multi-fund was enabled elsewhere. Prefer
  // it when present so the enable banner never shows for an enabled school.
  const multiFundEnabled = dashboard
    ? !!dashboard.multi_fund_enabled
    : !!school?.settings?.finance?.multi_fund_enabled;

  // The current term's own start/end dates, when the school has configured them.
  const termRange = useMemo(() => {
    const current = (school?.settings?.terms || []).find(t => t.term === schoolTerm);
    if (!current?.start_date || !current?.end_date) return null;
    return { start: isoDatePart(current.start_date), end: isoDatePart(current.end_date) };
  }, [school, schoolTerm]);

  const applyPreset = (next) => {
    setPreset(next);
    if (next === RANGE_PRESETS.TERM && termRange) {
      setStartDate(termRange.start);
      setEndDate(termRange.end);
    } else if (next === RANGE_PRESETS.SIX_MONTHS) {
      setStartDate(toInputDate(startOfMonthsAgo(5)));
      setEndDate(toInputDate(new Date()));
    } else if (next === RANGE_PRESETS.ONE_YEAR) {
      setStartDate(toInputDate(startOfMonthsAgo(11)));
      setEndDate(toInputDate(new Date()));
    }
  };

  // Current term is the default, but school settings load after first render —
  // apply it once they arrive. Falls back to the 6-month view for schools that
  // haven't set term dates yet.
  useEffect(() => {
    if (!school || preset !== RANGE_PRESETS.TERM) return;
    if (termRange) {
      setStartDate(termRange.start);
      setEndDate(termRange.end);
    } else {
      applyPreset(RANGE_PRESETS.SIX_MONTHS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school, termRange, preset]);

  // Editing a date by hand no longer matches whichever preset was selected.
  const setCustomStart = (value) => { setStartDate(value); setPreset(RANGE_PRESETS.CUSTOM); };
  const setCustomEnd = (value) => { setEndDate(value); setPreset(RANGE_PRESETS.CUSTOM); };

  const hasRange = Boolean(startDate && endDate);

  // Match the chart's span to the selected range so the trend covers the same
  // period as the figures above it. The API caps trends at 24 months.
  const spannedMonths = useMemo(
    () => (hasRange ? monthsBetween(startDate, endDate) : null),
    [hasRange, startDate, endDate]
  );
  const trendMonths = spannedMonths
    ? Math.min(MAX_TREND_MONTHS, Math.max(1, spannedMonths))
    : DEFAULT_TREND_MONTHS;
  const isTrendCapped = Boolean(spannedMonths && spannedMonths > MAX_TREND_MONTHS);

  const fetchDashboard = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ trendMonths: String(trendMonths) });
      if (fundId) params.set('fund_id', fundId);
      if (startDate) params.set('startDate', new Date(startDate).toISOString().slice(0, 10));
      if (endDate) params.set('endDate', new Date(endDate).toISOString().slice(0, 10));
      const { data } = await userRequest.get(`/schools/${schoolId}/finance/dashboard?${params.toString()}`);
      setDashboard(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, fundId, startDate, endDate, trendMonths]);

  // Wallet balance — the school's spendable cash, independent of multi-fund mode.
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const { data } = await userRequest.get(`banking/wallets/${schoolId}/balance`);
        setBalance(data.data ?? data);
      } catch (error) {
        console.log(error);
      }
    };
    // Keyed on schoolId only: the endpoint doesn't take an account number, and
    // gating on one hid the balance entirely for schools whose deposit account
    // hasn't been provisioned yet.
    if (schoolId) fetchBalance();
  }, [schoolId]);

  useEffect(() => {
    const fetchPayout = async () => {
      try {
        const { data } = await userRequest.get(`/transactions/school/${schoolId}/payout-summary`);
        setPayout(data);
      } catch (error) {
        console.log(error);
      }
    };
    if (schoolId) fetchPayout();
  }, [schoolId]);

  // Fee collection for the current term — how much of what was invoiced has come in.
  useEffect(() => {
    const fetchPaymentSummary = async () => {
      try {
        const { data } = await userRequest.get(
          `schools/${schoolId}/payment-summary?term=${schoolTerm}&session=${encodeURIComponent(schoolSession)}`
        );
        setPaymentSummary(data);
      } catch (error) {
        console.log(error);
      }
    };
    if (schoolId && schoolTerm && schoolSession) fetchPaymentSummary();
  }, [schoolId, schoolTerm, schoolSession]);

  const copyAccountNumber = () => {
    if (!schoolAccount) return;
    navigator.clipboard?.writeText(schoolAccount);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const summary = dashboard?.summary || {};
  const funds = dashboard?.funds || [];
  // trendMonths only tells the API how many monthly buckets to return, not
  // where the window sits, so clip to the selected range. Months are 'YYYY-MM'
  // and sort lexicographically, so plain string comparison is enough. This is a
  // no-op when the API already honours the range, and stops the chart plotting
  // months outside what was asked for when the range ends in the past.
  const visibleTrends = useMemo(() => {
    const all = dashboard?.trends || [];
    if (!hasRange) return all;
    const from = startDate.slice(0, 7);
    const to = endDate.slice(0, 7);
    return all.filter(t => t.month >= from && t.month <= to);
  }, [dashboard, hasRange, startDate, endDate]);
  const warnings = (dashboard?.warnings || []).filter(w => !dismissedWarnings.includes(w.code));

  // Fee collection figures. `totalAmount` is what was invoiced this term, so the
  // collection rate measures against it; carried-over debt is shown separately
  // because it was never part of this term's invoice.
  const invoiced = Number(paymentSummary?.totalAmount) || 0;
  const collected = Number(paymentSummary?.totalAmountPaid) || 0;
  const carriedOver = Number(paymentSummary?.totalPreviousOutstandingAmount) || 0;
  const currentOutstanding = Number(paymentSummary?.totalCurrentOutstandingAmount) || 0;
  const discount = Number(paymentSummary?.discountAmount) || 0;
  const collectionRate = invoiced > 0 ? Math.min(100, Math.round((collected / invoiced) * 100)) : 0;

  const quickActions = [
    { to: '/withdraw', icon: FiArrowUpRight, color: 'indigo', label: 'Withdraw', desc: 'Move money to your bank account' },
    { to: '/finance/expenses', icon: FiSend, color: 'blue', label: 'Transfer', desc: 'Send money to any bank account' },
    { onClick: () => setIsStatementOpen(true), icon: FiFileText, color: 'slate', label: 'Statement', desc: 'Email an account statement' },
    { to: '/finance/expenses', icon: FiTrendingDown, color: 'red', label: 'Expenses', desc: 'Record and review spending' },
    { to: '/class-payments', icon: FiUsers, color: 'green', label: 'Student invoices', desc: 'Track fees class by class' },
    { to: '/finance/reports', icon: FiPieChart, color: 'amber', label: 'Reports', desc: 'Download financial statements' },
  ];

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <Header
        category="Finance"
        title="Money overview"
        description="What you have, what parents still owe, and where your money is going."
      />

      {!multiFundEnabled && (
        <EnableMultiFundBanner schoolId={schoolId} onEnabled={() => { refreshSchool(); fetchDashboard(); }} />
      )}

      {warnings.map(w => (
        <div key={w.code} className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm mb-4 ${severityStyles[w.severity] || severityStyles.info}`}>
          <div className="flex items-start gap-2">
            <FiAlertTriangle className="mt-0.5 shrink-0" />
            {/* The API already embeds any count in `message` ("12 active
                student(s) have..."), so appending w.count repeats it. */}
            <span>{w.message}</span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {/* Every warning the API raises is about fund setup, so give it a
                way out — otherwise Dismiss is the only option on a message
                that describes something the admin is expected to fix. */}
            <Link to="/finance/funds" className="text-xs font-semibold underline whitespace-nowrap">
              Manage funds
            </Link>
            <button
              type="button"
              onClick={() => setDismissedWarnings(prev => [...prev, w.code])}
              className="text-xs font-medium underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}

      {/* ── What you have right now ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        {/* Available balance + the account parents pay into */}
        <div className="lg:col-span-2 rounded-2xl border border-indigo-100 bg-indigo-50 p-6 flex flex-col">
          <p className="text-xs font-medium text-indigo-700/70 uppercase tracking-wide">Available balance</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {balance ? formatCurrency(balance.availableBalance) : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Money you can withdraw or transfer right now.</p>

          <div className="flex items-center gap-2 mt-4">
            <Link to="/withdraw" className="flex-1">
              <button className="w-full py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors">
                Withdraw
              </button>
            </Link>
            <Link to="/finance/expenses" className="flex-1">
              <button className="w-full py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 hover:bg-indigo-100 rounded-full transition-colors">
                Transfer
              </button>
            </Link>
          </div>

          {schoolAccount && (
            <div className="mt-5 pt-4 border-t border-indigo-100">
              <p className="text-xs text-gray-500 mb-1.5">Receive money into this account</p>
              <div className="flex items-center gap-2">
                <span className="font-mono text-base font-semibold text-gray-900">{schoolAccount}</span>
                <button
                  type="button"
                  onClick={copyAccountNumber}
                  title="Copy account number"
                  className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-100 transition-colors"
                >
                  {copied ? <FiCheck className="w-3.5 h-3.5" /> : <FiCopy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                {school?.bank?.bankName || 'N/A'} · {school?.bank?.accountName || school?.account_name}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <img src={Icons.ndicLogo} alt="" className="h-5" />
                <p className="text-[11px] text-gray-400 leading-snug">
                  Deposits are NDIC insured. Fee payments land here instantly.
                </p>
              </div>
            </div>
          )}

          {/* Without a provisioned deposit account there is nothing to show, but
              silently dropping the section leaves the admin wondering where it
              went — so say so instead. */}
          {!schoolAccount && (
            <div className="mt-5 pt-4 border-t border-indigo-100">
              <p className="text-xs text-gray-500 mb-1">No deposit account yet</p>
              <p className="text-[11px] text-gray-400 leading-snug">
                Your account for receiving fee payments is still being set up.
                Contact support if it doesn't appear shortly.
              </p>
            </div>
          )}
        </div>

        {/* Fee collection — the signature: one bar, one rate, one question answered */}
        <div className="lg:col-span-3 rounded-2xl border border-gray-200 p-6">
          <div className="flex items-start justify-between gap-3 mb-5">
            <div>
              <p className="text-sm font-semibold text-gray-800">Fee collection</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {schoolTerm ? `${displaySchoolTerm(schoolTerm)} · ${schoolSession}` : 'Current term'}
              </p>
            </div>
            <Link to="/class-payments" className="text-xs font-medium text-indigo-600 hover:text-indigo-700 shrink-0">
              View by class →
            </Link>
          </div>

          {invoiced === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-gray-400">No invoices generated for this term yet.</p>
              <Link to="/class-payments" className="inline-block mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-700">
                Generate invoices
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-2xl font-bold text-gray-900">{collectionRate}%</p>
                <p className="text-xs text-gray-500">
                  of {formatCurrency(invoiced)} invoiced
                </p>
              </div>

              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-3 bg-emerald-500 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${collectionRate}%` }}
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <p className="text-xs text-gray-500">Collected</p>
                  </div>
                  <p className="text-sm font-bold text-emerald-700 mt-1">{formatCurrency(collected)}</p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-gray-300" />
                    <p className="text-xs text-gray-500">Still owed</p>
                  </div>
                  <p className="text-sm font-bold text-red-600 mt-1">{formatCurrency(currentOutstanding)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Carried over</p>
                  <p className={`text-sm font-bold mt-1 ${carriedOver > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                    {formatCurrency(carriedOver)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Discounts</p>
                  <p className="text-sm font-bold text-gray-700 mt-1">{formatCurrency(discount)}</p>
                </div>
              </div>

              {carriedOver > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-4">
                  {formatCurrency(carriedOver)} is unpaid debt carried over from a previous term. It is not part of this term's invoice.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Where the money is going ────────────────────────────────── */}
      <div className="border border-gray-200 rounded-2xl p-5 mb-4">
        <div className="mb-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Cash flow</p>
              <p className="text-xs text-gray-500 mt-0.5">Money recorded in and out of your funds.</p>
            </div>
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full">
              {[
                { key: RANGE_PRESETS.TERM, label: 'Current term', disabled: !termRange },
                { key: RANGE_PRESETS.SIX_MONTHS, label: '6 months' },
                { key: RANGE_PRESETS.ONE_YEAR, label: '1 year' },
                ...(preset === RANGE_PRESETS.CUSTOM ? [{ key: RANGE_PRESETS.CUSTOM, label: 'Custom' }] : []),
              ].map(o => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => applyPreset(o.key)}
                  disabled={o.disabled}
                  title={o.disabled ? 'Set start and end dates for this term in Settings' : undefined}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    preset === o.key
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  } ${o.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <div className="w-40">
              <FundSelector schoolId={schoolId} value={fundId} onChange={setFundId} includeAllOption />
            </div>
            <input
              type="date" value={startDate} max={endDate || undefined}
              onChange={e => setCustomStart(e.target.value)}
              className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-3 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date" value={endDate} min={startDate || undefined}
              onChange={e => setCustomEnd(e.target.value)}
              className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-3 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Money in" value={formatCurrency(summary.total_income)} icon={FiTrendingUp} color="green" />
          <StatCard label="Money out" value={formatCurrency(summary.total_expenses)} icon={FiTrendingDown} color="red" />
          <StatCard label="Net" value={formatCurrency(summary.net)} icon={FiDollarSign} color="indigo" />
          <StatCard
            label="Withdrawn to bank"
            value={payout ? customFormatter(payout.totalPayout) : '—'}
            icon={FiArrowUpRight}
            color="blue"
          />
        </div>

        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <p className="text-xs font-medium text-gray-500">
            {preset === RANGE_PRESETS.TERM && termRange
              ? `Income vs expenses · ${displaySchoolTerm(schoolTerm)} ${schoolSession || ''}`
              : preset === RANGE_PRESETS.SIX_MONTHS
                ? 'Income vs expenses · last 6 months'
                : preset === RANGE_PRESETS.ONE_YEAR
                  ? 'Income vs expenses · last 12 months'
                  : hasRange
                    ? `Income vs expenses · ${formatMonth(startDate)} – ${formatMonth(endDate)}`
                    : `Income vs expenses, last ${DEFAULT_TREND_MONTHS} months`}
          </p>
          {isTrendCapped && (
            <p className="text-xs text-amber-600">
              Showing the most recent {MAX_TREND_MONTHS} months — the longest span the chart supports.
            </p>
          )}
        </div>
        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">Loading chart…</div>
        ) : visibleTrends.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">
            No income or expenses recorded yet for this period.
          </div>
        ) : (
          <FinanceTrendChart trends={visibleTrends} />
        )}
      </div>

      {/* ── Where the money sits ────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-2xl p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-sm font-semibold text-gray-800">Fund balances</p>
            <p className="text-xs text-gray-500 mt-0.5">Separate pots you can split school money into.</p>
          </div>
          <Link to="/finance/funds" className="text-xs font-medium text-indigo-600 hover:text-indigo-700 shrink-0">
            Manage funds →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {funds.length === 0 ? (
            <p className="text-sm text-gray-400">
              {multiFundEnabled ? 'No funds created yet.' : 'Enable multi-fund finance to split money into funds.'}
            </p>
          ) : funds.map(f => {
            const card = (
              <StatCard
                label={f.name}
                value={formatCurrency(f.availableBalance)}
                icon={FiLayers}
                color={f.is_default ? 'indigo' : 'gray'}
                compact
                interactive={Boolean(f.fund_id)}
              />
            );
            // The dashboard returns fund_id rather than _id. Guard on it so a
            // fund without one renders as a plain card instead of linking to
            // /finance/funds/undefined.
            return f.fund_id ? (
              <Link key={f.fund_id} to={`/finance/funds/${f.fund_id}`} className="block">
                {card}
              </Link>
            ) : (
              <React.Fragment key={f.name}>{card}</React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── What you can do ─────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-2xl p-5">
        <p className="text-sm font-semibold text-gray-800 mb-4">Quick actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickActions.map(({ to, onClick, icon: Icon, color, label, desc }) => {
            const body = (
              <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl border border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors h-full text-left">
                <div className={`p-2 rounded-lg shrink-0 ${ACTION_COLORS[color]}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                </div>
              </div>
            );
            return to ? (
              <Link key={label} to={to}>{body}</Link>
            ) : (
              <button key={label} type="button" onClick={onClick} className="w-full">{body}</button>
            );
          })}
        </div>
      </div>

      {isStatementOpen && (
        <StatementModal closeModal={() => setIsStatementOpen(false)} school={school} />
      )}
    </div>
  );
};

export default FinanceDashboard;
